import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import API from '../../API/API';
import { PTRangePicker } from '../../Components/PTDatePicker/PTDatePicker';
import { formatPTDateTime, rangePT, toApiDays, zoneAbbrPT } from '../../utils/usTime';
import { formatMoney } from '../../utils/money';

const { Title, Text } = Typography;
const { Option } = Select;

// Read-only lead list for the MARKETING role (Essential Performance Marketing
// Columns). The API returns no customer name, phone or email.

const LEAD_STAGES = [
  'New', 'Call Back', 'Voice Mail', 'Follow-Up', 'Quoted', 'Negotiation',
  'Pending Payment', 'Payment Completed', 'Sales Made', 'Part Not Available',
  'Wrong Number', 'Not Interested', 'Hang Up', 'Fake', 'Test Lead', 'Error',
];

const PAYMENT_COLORS = {
  'Unpaid': 'red',
  'Partially Paid': 'orange',
  'Fully Paid': 'green',
  'Completed': 'green',
};

const dash = (value) => (value === undefined || value === null || value === '' ? '—' : value);
const money = (value) => (value === undefined || value === null ? '—' : formatMoney(value));

const ellipsisCell = (value, width = 220) => (
  value ? (
    <Tooltip title={value} placement="topLeft">
      <div style={{ maxWidth: width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </Tooltip>
  ) : '—'
);

const columns = [
  {
    title: `Created At (${zoneAbbrPT()})`,
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 170,
    fixed: 'left',
    render: (value) => formatPTDateTime(value),
  },
  { title: 'Domain', dataIndex: 'domain', key: 'domain', width: 210, render: (v) => ellipsisCell(v, 200) },
  { title: 'Campaign Name', dataIndex: 'campaignName', key: 'campaignName', width: 200, render: (v) => ellipsisCell(v, 190) },
  {
    title: 'Landing URL',
    dataIndex: 'landingUrl',
    key: 'landingUrl',
    width: 240,
    render: (v) => ellipsisCell(v, 230),
  },
  { title: 'Google Click ID', dataIndex: 'googleClickId', key: 'googleClickId', width: 200, render: (v) => ellipsisCell(v, 190) },
  { title: 'Lead Stage', dataIndex: 'leadStage', key: 'leadStage', width: 150, render: (v) => (v ? <Tag color="blue">{v}</Tag> : '—') },
  { title: 'Status', dataIndex: 'status', key: 'status', width: 150, render: dash },
  { title: 'Score', dataIndex: 'score', key: 'score', width: 80, align: 'right', render: dash },
  {
    title: 'Existing Customer',
    dataIndex: 'isExistingCustomer',
    key: 'isExistingCustomer',
    width: 130,
    render: (v) => (v ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag>),
  },
  { title: 'Part', dataIndex: 'part', key: 'part', width: 160, render: (v) => ellipsisCell(v, 150) },
  {
    title: 'Year / Make / Model',
    key: 'vehicle',
    width: 220,
    render: (_, row) => ellipsisCell([row.year, row.make, row.model].filter(Boolean).join(' / '), 210),
  },
  { title: 'Quoted Value', dataIndex: 'quotedValue', key: 'quotedValue', width: 130, align: 'right', render: money },
  {
    title: 'Payment Status',
    dataIndex: 'paymentStatus',
    key: 'paymentStatus',
    width: 140,
    render: (v) => (v ? <Tag color={PAYMENT_COLORS[v] || 'default'}>{v}</Tag> : '—'),
  },
  { title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount', width: 130, align: 'right', render: money },
  { title: 'Paid Amount', dataIndex: 'paidAmount', key: 'paidAmount', width: 130, align: 'right', render: money },
  { title: 'Region', dataIndex: 'region', key: 'region', width: 140, render: dash },
  { title: 'City', dataIndex: 'city', key: 'city', width: 140, render: dash },
  { title: 'Zip Code', dataIndex: 'zipCode', key: 'zipCode', width: 100, render: dash },
  { title: 'Platform', dataIndex: 'platform', key: 'platform', width: 120, render: dash },
];

const defaultRange = () => rangePT('last30Days');

const MarketingLeads = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState(defaultRange);
  const [domains, setDomains] = useState([]);
  const [domain, setDomain] = useState(undefined);
  const [leadStage, setLeadStage] = useState(undefined);
  const [existingCustomer, setExistingCustomer] = useState(undefined);
  const [campaignInput, setCampaignInput] = useState('');
  const [campaign, setCampaign] = useState('');

  // Campaign search is sent 0.5 s after typing stops.
  useEffect(() => {
    const timer = setTimeout(() => {
      setCampaign(campaignInput.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [campaignInput]);

  useEffect(() => {
    let active = true;
    API.getMarketingLeadDomains().then((response) => {
      if (active && response?.status && Array.isArray(response.data)) {
        setDomains(response.data);
      }
    });
    return () => { active = false; };
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const [startDate, endDate] = toApiDays(dateRange);
      const params = {
        page,
        limit: pageSize,
        ...(startDate && endDate ? { startDate, endDate } : {}),
        ...(domain ? { domain } : {}),
        ...(leadStage ? { leadStage } : {}),
        ...(existingCustomer ? { isExistingCustomer: existingCustomer } : {}),
        ...(campaign ? { campaign } : {}),
      };
      const response = await API.getMarketingLeads(params);
      if (response?.status) {
        setRows(response.data || []);
        setTotal(response.pagination?.total || 0);
      } else {
        setRows([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, dateRange, domain, leadStage, existingCustomer, campaign]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const resetFilters = () => {
    setDateRange(defaultRange());
    setDomain(undefined);
    setLeadStage(undefined);
    setExistingCustomer(undefined);
    setCampaignInput('');
    setCampaign('');
    setPage(1);
  };

  return (
    <Card variant="borderless" styles={{ body: { padding: 0 } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>My Leads</Title>
          <Text type="secondary">All leads (read-only) · {total} in the selected range</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadLeads} loading={loading}>Refresh</Button>
      </div>

      <Space wrap size={12} style={{ marginBottom: 16 }}>
        <PTRangePicker
          value={dateRange}
          allowClear={false}
          onChange={(value) => {
            setDateRange(value && value[0] && value[1] ? value : defaultRange());
            setPage(1);
          }}
          presets={[
            { label: 'Today', value: rangePT('today') },
            { label: 'Yesterday', value: rangePT('yesterday') },
            { label: 'Last 7 Days', value: rangePT('last7Days') },
            { label: 'Last 30 Days', value: rangePT('last30Days') },
            { label: 'This Month', value: rangePT('thisMonth') },
            { label: 'Last Month', value: rangePT('lastMonth') },
          ]}
        />
        <Select
          allowClear
          showSearch
          placeholder="All domains"
          style={{ width: 240 }}
          value={domain}
          onChange={(value) => { setDomain(value); setPage(1); }}
        >
          {domains.map((d) => <Option key={d} value={d}>{d}</Option>)}
        </Select>
        <Select
          allowClear
          placeholder="All lead stages"
          style={{ width: 180 }}
          value={leadStage}
          onChange={(value) => { setLeadStage(value); setPage(1); }}
        >
          {LEAD_STAGES.map((stage) => <Option key={stage} value={stage}>{stage}</Option>)}
        </Select>
        <Select
          allowClear
          placeholder="New + existing customers"
          style={{ width: 210 }}
          value={existingCustomer}
          onChange={(value) => { setExistingCustomer(value); setPage(1); }}
        >
          <Option value="false">New customers only</Option>
          <Option value="true">Existing customers only</Option>
        </Select>
        <Input
          allowClear
          placeholder="Search campaign name"
          style={{ width: 220 }}
          value={campaignInput}
          onChange={(e) => setCampaignInput(e.target.value)}
        />
        <Button onClick={resetFilters}>Reset</Button>
      </Space>

      <Table
        rowKey="_id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count}`,
          onChange: (nextPage, nextSize) => {
            if (nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          },
        }}
      />
    </Card>
  );
};

export default MarketingLeads;
